import { useEffect, useState } from 'preact/hooks';
import { similarPrices, type PriceHistoryBody, type PriceRow } from '../../shared/prices/history';
import { normaliseItem, variancePct } from '../../shared/quotes/rules';
import { useI18n } from '../i18n';
import { apiGet } from '../lib/api';
import { formatDate } from '../lib/dates';

// The CFO's price check (D89): each PO line against the last time the same item was bought,
// from the price history (D72). One read of the request's category; CFO and Procurement only.

const money = (minor: number) =>
  new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(
    minor / 100,
  );

/** A rise above this many percent is shown as a warning. */
const WARN_PCT = 10;

/** Last price in the PO's currency: the same currency, or the SAR price for a SAR PO. */
function comparable(row: PriceRow, currency: string): number | null {
  if (row.currency === currency) return row.unitPrice;
  if (currency === 'SAR' && row.unitPriceSAR !== null) return row.unitPriceSAR;
  return null;
}

interface Props {
  /** This request: its own chosen quote is already in the price history, so it is left out. */
  prNo: string;
  category: string;
  currency: string;
  lines: { description: string; unitPrice: number }[];
}

type Load = { kind: 'loading' } | { kind: 'error' } | { kind: 'ok'; data: PriceHistoryBody };

export function PriceCompare({ prNo, category, currency, lines }: Props) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });

  useEffect(() => {
    if (!category) return;
    let live = true;
    apiGet<PriceHistoryBody>(`/api/prices?category=${encodeURIComponent(category)}`)
      .then((data) => live && setLoad({ kind: 'ok', data }))
      .catch(() => live && setLoad({ kind: 'error' }));
    return () => {
      live = false;
    };
  }, [category]);

  if (!category || lines.length === 0) return null;
  const past = load.kind === 'ok' ? load.data.rows.filter((r) => r.prNo !== prNo) : [];

  return (
    <section class="card card--insight stack" aria-labelledby="cfo-prices">
      <div class="card__head">
        <h3 id="cfo-prices" class="card__title">
          {t('pcmp.title')}
        </h3>
        <a href="#/prices">{t('lp.all')}</a>
      </div>
      {load.kind === 'loading' && <p aria-busy="true">{t('req.loading')}</p>}
      {load.kind === 'error' && <p role="alert">{t('lp.error')}</p>}
      {load.kind === 'ok' && (
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th scope="col">{t('cfo.item')}</th>
                <th scope="col" class="num">
                  {t('pcmp.thisPo')}
                </th>
                <th scope="col" class="num">
                  {t('pcmp.last')}
                </th>
                <th scope="col">{t('pcmp.when')}</th>
                <th scope="col">{t('pcmp.change')}</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((l, i) => {
                const last = similarPrices(past, l.description, normaliseItem, 1)[0];
                const lastPrice = last ? comparable(last, currency) : null;
                const pct = lastPrice === null ? null : variancePct(l.unitPrice, lastPrice);
                const tone =
                  pct === null
                    ? 'neutral'
                    : pct > WARN_PCT
                      ? 'warning'
                      : pct <= 0
                        ? 'success'
                        : 'info';
                return (
                  <tr key={i}>
                    <td dir="auto">
                      {l.description}
                      {last && (
                        <span class="caption cell-sub" dir="auto">
                          {t('pcmp.asBought', { item: last.item })}
                        </span>
                      )}
                    </td>
                    <td class="num nowrap" dir="ltr">
                      {currency} {money(l.unitPrice)}
                    </td>
                    <td class="num nowrap" dir="ltr">
                      {last ? `${last.currency} ${money(last.unitPrice)}` : '—'}
                    </td>
                    <td>
                      {last ? (
                        <>
                          {last.date ? formatDate(last.date, lang) : '—'}
                          <span class="caption cell-sub" dir="auto">
                            {last.supplierName || '—'}
                            {last.poNo && (
                              <>
                                {' · '}
                                <span class="ref" dir="ltr">
                                  {last.poNo}
                                </span>
                              </>
                            )}
                          </span>
                        </>
                      ) : (
                        <span class="caption">{t('lp.none')}</span>
                      )}
                    </td>
                    <td>
                      {pct === null ? (
                        <span class="caption">{last ? t('pcmp.otherCurrency') : '—'}</span>
                      ) : (
                        <span class={`pill pill--${tone}`} dir="ltr">
                          {pct > 0 ? '+' : ''}
                          {pct}%{' '}
                          {pct > 0 ? t('pcmp.higher') : pct < 0 ? t('pcmp.lower') : t('pcmp.same')}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
